'use strict';

import { getLegacyPatients } from './patientsApi';
import { DEFAULT_RECORDED_TIMEZONE } from './dateFormat';

/**
 * Legacy lab datasets (imported 2019–2020 penguin gait trials) are listed on
 * their own "Legacy analytics" page from the `/a/legacy/...` list routes. Routes
 * keyed by trial id (review, angles, video, annotations) serve them unchanged.
 *
 * A trial is legacy while its research `tags` contain LEGACY_TRIAL_TAG; removing
 * that tag moves it back to the main Analytics list, so the import tags are
 * never shown as chips or offered for editing.
 */

export const LEGACY_TRIAL_TAG = 'penguin-legacy';
export const LEGACY_SOURCE_TAG_PREFIX = 'penguin:gait/';
export const LEGACY_BAD_TRIANGULATION_TAG = 'penguin-bad-triangulation';

/** Legacy video and angle rows share an exact 60 Hz clock: sample i is at i / 60 s. */
export const LEGACY_FPS = 60;

export const LEGACY_ROOT_PATH = '/legacy';

export function legacyDashboardPath(patientId) {
  return `${LEGACY_ROOT_PATH}/patient_dashboard/${patientId}`;
}

export function legacyReviewPath(patientId, trialId) {
  return `${LEGACY_ROOT_PATH}/review_patient/${patientId}/${trialId}`;
}

/**
 * Angle columns the import fills. Heel flex, knee rotation, neck, midhip, floor,
 * contact and pose-derived columns stay null on every legacy trial.
 */
export const LEGACY_ANGLE_METRICS = [
  'hip_right_flex', 'hip_left_flex',
  'knee_right_flex', 'knee_left_flex',
  'hip_right_abduct', 'hip_left_abduct',
  'hip_right_rot', 'hip_left_rot',
  'pelvic_tilt_left', 'pelvic_tilt_right',
  'pelvic_obl_left', 'pelvic_obl_right',
  'pelvic_rot_left', 'pelvic_rot_right',
  'shoulder_right_flex', 'shoulder_left_flex',
  'shoulder_right_abduct', 'shoulder_left_abduct',
  'shoulder_right_rot', 'shoulder_left_rot',
  'elbow_right_flex', 'elbow_left_flex',
];

function tagList(tags) {
  return Array.isArray(tags) ? tags.filter((tag) => typeof tag === 'string') : [];
}

export function isLegacyTrial(trial) {
  return tagList(trial?.tags).includes(LEGACY_TRIAL_TAG);
}

/** Import tags: kept on every save, never rendered as chips or edited. */
export function isProtectedLegacyTag(tag) {
  if (typeof tag !== 'string') return false;
  return (
    tag === LEGACY_TRIAL_TAG ||
    tag === LEGACY_BAD_TRIANGULATION_TAG ||
    tag.startsWith(LEGACY_SOURCE_TAG_PREFIX)
  );
}

export function editableLegacyTags(tags) {
  return tagList(tags).filter((tag) => !isProtectedLegacyTag(tag));
}

export function hasBadTriangulation(trial) {
  return tagList(trial?.tags).includes(LEGACY_BAD_TRIANGULATION_TAG);
}

/** Edited free-form tags plus the import tags the trial already carried. */
export function restoreProtectedLegacyTags(editedTags, originalTags) {
  return [
    ...tagList(originalTags).filter(isProtectedLegacyTag),
    ...editableLegacyTags(editedTags),
  ];
}

const LEGACY_NAME_PATTERN = /(\d{4}-\d{2}-\d{2})--(\d{2})-(\d{2})-(\d{2})/;

/** Recording instant in ms; falls back to the `Legacy YYYY-MM-DD--HH-MM-SS` name. */
export function trialTimeMs(trial) {
  const date = trial?.date;
  if (date != null && date !== '') {
    const ms = Date.parse(String(date).trim().replace(' ', 'T'));
    if (Number.isFinite(ms)) return ms;
  }
  const match = LEGACY_NAME_PATTERN.exec(String(trial?.name ?? ''));
  if (!match) return null;
  return Date.parse(`${match[1]}T${match[2]}:${match[3]}:${match[4]}Z`);
}

function compareTimes(left, right, direction) {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  return direction * (left - right);
}

export function compareTrialsByDateDesc(a, b) {
  return compareTimes(trialTimeMs(a), trialTimeMs(b), -1);
}

export function sessionKeyOf(trial) {
  const key = trial?.session ?? trial?.session_key;
  return key == null ? '' : String(key);
}

/**
 * Trials bucketed by lab session, newest session first. Inside a session the
 * trials run in recording order. Trials without a session key share one group.
 */
export function groupTrialsBySession(trials) {
  const groups = new Map();
  (Array.isArray(trials) ? trials : []).forEach((trial) => {
    if (!trial) return;
    const key = sessionKeyOf(trial);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(trial);
  });
  const out = [];
  groups.forEach((list, sessionKey) => {
    const sorted = list.slice().sort((a, b) => compareTimes(trialTimeMs(a), trialTimeMs(b), 1));
    out.push({ sessionKey, trials: sorted, startMs: trialTimeMs(sorted[0]) });
  });
  out.sort((a, b) => compareTimes(a.startMs, b.startMs, -1));
  return out;
}

/** Patient rows with trial / session counts and the recording date range. */
export function summarizeLegacyPatients(patients, trials) {
  const allTrials = Array.isArray(trials) ? trials.filter(Boolean) : [];
  return (Array.isArray(patients) ? patients : []).filter(Boolean).map((patient) => {
    const own = allTrials.filter(
      (trial) => String(trial.patient_id) === String(patient.patient_id)
    );
    const sessions = new Set(own.map(sessionKeyOf).filter(Boolean));
    const times = own.map(trialTimeMs).filter((ms) => ms != null);
    return {
      ...patient,
      trialCount: own.length,
      sessionCount: sessions.size,
      firstMs: times.length ? Math.min(...times) : null,
      lastMs: times.length ? Math.max(...times) : null,
      timezone: own.map((trial) => trial.recorded_timezone).find(Boolean) || null,
      badTriangulationCount: own.filter(hasBadTriangulation).length,
    };
  });
}

/** `Oct 29, 2020` at the recording site; an invalid zone falls back to the default. */
export function formatLegacyDate(ms, timeZone) {
  if (ms == null) return '—';
  const opts = { year: 'numeric', month: 'short', day: 'numeric' };
  try {
    return new Intl.DateTimeFormat('en-US', {
      ...opts,
      timeZone: timeZone || DEFAULT_RECORDED_TIMEZONE,
    }).format(new Date(ms));
  } catch (err) {
    return new Intl.DateTimeFormat('en-US', {
      ...opts,
      timeZone: DEFAULT_RECORDED_TIMEZONE,
    }).format(new Date(ms));
  }
}

/** Natural order, so `Penguin S7` sorts before `Penguin S10`. */
export function comparePatientNames(a, b) {
  return String(a?.patient_name ?? '').localeCompare(String(b?.patient_name ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

let availability = { token: undefined, promise: null };

/**
 * True when the signed-in account has legacy patients. One request per token,
 * shared by every caller, so the sidebar does not refetch on each mount.
 */
export function loadLegacyAvailability() {
  const token = localStorage.getItem('token');
  if (availability.promise && availability.token === token) return availability.promise;
  const promise = getLegacyPatients().then((list) => Array.isArray(list) && list.length > 0);
  availability = { token, promise };
  return promise;
}

export function resetLegacyAvailability() {
  availability = { token: undefined, promise: null };
}
